import { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Link,
  InputAdornment,
  IconButton,
  Divider,
} from "@mui/material";
import { useAuth } from "../context/AuthContext";
import { useThemeMode } from "../context/ThemeContext";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import LiquidGlassCard from "../components/LiquidGlassCard";
import { getApiErrorMessage } from "../utils/apiError";

export default function Login() {
  const { mode } = useThemeMode();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [sessionConflict, setSessionConflict] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setSessionConflict(false);
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      if (err.response?.status === 409) setSessionConflict(true);
      setError(getApiErrorMessage(err, "Invalid email or password"));
    } finally {
      setLoading(false);
    }
  };

  const handleForceLogin = async () => {
    setError("");
    setLoading(true);
    try {
      await login(email, password, { force: true });
      navigate("/");
    } catch (err) {
      setError(getApiErrorMessage(err, "Unable to clear previous sessions."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: "flex", height: "100vh", width: "100vw", overflow: "hidden" }}>

      {/* ── Left Branding Panel ── */}
      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          justifyContent: "space-between",
          width: "50%",
          minWidth: 440,
          background: "linear-gradient(145deg, #060e1e 0%, #0a1628 40%, #0e1f40 70%, #0d1835 100%)",
          position: "relative",
          overflow: "hidden",
          p: "40px 48px",
        }}
      >
        {/* Glow orbs */}
        <Box sx={{ position: "absolute", width: 600, height: 600, borderRadius: "50%", background: "radial-gradient(circle, rgba(242,101,34,0.18) 0%, transparent 65%)", top: -160, right: -180, pointerEvents: "none" }} />
        <Box sx={{ position: "absolute", width: 400, height: 400, borderRadius: "50%", background: "radial-gradient(circle, rgba(242,101,34,0.10) 0%, transparent 65%)", bottom: -80, left: -120, pointerEvents: "none" }} />
        <Box sx={{ position: "absolute", width: 200, height: 200, borderRadius: "50%", background: "radial-gradient(circle, rgba(242,101,34,0.12) 0%, transparent 65%)", bottom: "38%", right: "12%", pointerEvents: "none" }} />

        {/* Dot grid */}
        <Box sx={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(rgba(255,255,255,0.035) 1px, transparent 1px)", backgroundSize: "30px 30px", pointerEvents: "none" }} />

        {/* Top stripe accent */}
        <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, height: "3px", background: "linear-gradient(90deg, transparent 0%, #F26522 40%, #ff9a5c 70%, transparent 100%)" }} />

        {/* Logo */}
        <Box sx={{ position: "relative", zIndex: 1 }}>
          <img src="/edge-ai-logo-white.png" alt="Assist-Edgex" style={{ height: 28, objectFit: "contain" }} />
        </Box>

        {/* Hero text + floating card */}
        <Box sx={{ position: "relative", zIndex: 1 }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.8,
              bgcolor: "rgba(242,101,34,0.12)",
              border: "1px solid rgba(242,101,34,0.25)",
              borderRadius: "20px",
              px: 1.5,
              py: 0.5,
              mb: 2.5,
            }}
          >
            <AutoAwesomeIcon sx={{ fontSize: 14, color: "#F26522" }} />
            <Typography sx={{ color: "#F26522", fontSize: "0.72rem", fontWeight: 700, letterSpacing: 0.5 }}>
              ENTERPRISE AI PLATFORM
            </Typography>
          </Box>

          <Typography
            sx={{
              color: "#fff",
              fontWeight: 800,
              lineHeight: 1.18,
              mb: 2,
              fontSize: { md: "2.1rem", lg: "2.6rem" },
              letterSpacing: "-0.5px",
            }}
          >
            Agentic AI For Your{" "}
            <Box
              component="span"
              sx={{
                color: "#F26522",
                position: "relative",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  bottom: -4,
                  left: 0,
                  right: 0,
                  height: "3px",
                  background: "linear-gradient(90deg, #F26522, transparent)",
                  borderRadius: "2px",
                },
              }}
            >
              Business Operations
            </Box>
          </Typography>

          <Typography sx={{ color: "rgba(255,255,255,0.5)", mb: 4, fontSize: "0.9rem", lineHeight: 1.75, maxWidth: 400 }}>
            Automate smarter. Move faster. Scale effortlessly with the power of enterprise-grade AI automation.
          </Typography>

          {/* Feature pills */}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {["ISO 27001", "SOC 2", "GDPR", "HIPAA"].map((badge) => (
              <Box
                key={badge}
                sx={{
                  px: 1.4,
                  py: 0.4,
                  bgcolor: "rgba(255,255,255,0.05)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: "6px",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#4ade80" }} />
                <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: "0.7rem", fontWeight: 600 }}>
                  {badge}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Footer */}
        <Box sx={{ position: "relative", zIndex: 1 }}>
          <Typography sx={{ color: "rgba(255,255,255,0.2)", fontSize: "0.72rem" }}>
            © {new Date().getFullYear()} AutomationEdge Technologies. All rights reserved.
          </Typography>
        </Box>
      </Box>

      {/* ── Right Form Panel ── */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: mode === "light"
            ? "linear-gradient(135deg, #dde4f0 0%, #eaeef6 50%, #d8e2f2 100%)"
            : "linear-gradient(135deg, #050c1a 0%, #08122a 50%, #0b1628 100%)",
          px: { xs: 3, sm: 5 },
          py: 6,
          overflow: "hidden",
          position: "relative",
        }}
      >
        {/* Vivid colour blobs — the glass refracts/blurs these */}
        <Box sx={{ position: "absolute", width: 420, height: 420, borderRadius: "50%", background: "radial-gradient(circle, rgba(242,101,34,0.55) 0%, transparent 60%)", top: "-60px", right: "-60px", pointerEvents: "none" }} />
        <Box sx={{ position: "absolute", width: 320, height: 320, borderRadius: "50%", background: "radial-gradient(circle, rgba(80,140,255,0.45) 0%, transparent 60%)", bottom: "-40px", left: "-40px", pointerEvents: "none" }} />
        <Box sx={{ position: "absolute", width: 200, height: 200, borderRadius: "50%", background: "radial-gradient(circle, rgba(180,80,255,0.30) 0%, transparent 60%)", top: "55%", right: "18%", pointerEvents: "none" }} />
        {/* Dot grid */}
        <Box sx={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(rgba(0,0,0,0.08) 1px, transparent 1px)", backgroundSize: "22px 22px", pointerEvents: "none" }} />

        {/* Form card — liquid glass */}
        <LiquidGlassCard
          blurIntensity="xl"
          shadowIntensity="md"
          glowIntensity="md"
          borderRadius="20px"
          background={mode === "light" ? "rgba(255,255,255,0.25)" : "rgba(10,20,50,0.30)"}
          style={{
            width: "100%",
            maxWidth: 420,
            zIndex: 1,
          }}
        >
        <Box sx={{ p: { xs: 3.5, sm: 4.5 } }}>
          {/* Mobile logo */}
          <Box sx={{ display: { xs: "flex", md: "none" }, justifyContent: "center", mb: 3 }}>
            <img
              src={mode === "light" ? "/edge-ai-logo-black.png" : "/edge-ai-logo-white.png"}
              alt="Assist-Edgex"
              style={{ height: 26, objectFit: "contain" }}
            />
          </Box>

          <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5, color: mode === "light" ? "#0d1f3c" : "#f1f5f9", letterSpacing: "-0.3px" }}>
            Welcome back 👋
          </Typography>
          <Typography sx={{ color: "text.secondary", fontSize: "0.875rem", mb: 3.5 }}>
            Sign in to your AI workspace
          </Typography>

          {error && (
            <Box sx={{ bgcolor: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.2)", borderRadius: "10px", px: 2, py: 1.2, mb: 2.5 }}>
              <Typography color="error" variant="body2" sx={{ fontWeight: 600, fontSize: "0.82rem" }}>
                {error}
              </Typography>
            </Box>
          )}

          {sessionConflict && (
            <Button fullWidth variant="outlined" disabled={loading} onClick={handleForceLogin}
              sx={{ mb: 2, borderRadius: "10px", borderColor: "#F26522", color: "#F26522", textTransform: "none", fontWeight: 600, fontSize: "0.85rem", "&:hover": { bgcolor: "rgba(242,101,34,0.06)" } }}
            >
              Delete previous sessions and login
            </Button>
          )}

          <Box component="form" onSubmit={handleLogin}>
            {/* Email */}
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.7, fontSize: "0.82rem", color: mode === "light" ? "#374151" : "#94a3b8" }}>
              Email Address
            </Typography>
            <TextField
              required fullWidth id="email" placeholder="you@company.com"
              name="email" autoComplete="email" autoFocus variant="outlined"
              value={email} onChange={(e) => setEmail(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <MailOutlineIcon sx={{ color: "text.secondary", fontSize: 18 }} />
                    </InputAdornment>
                  ),
                },
              }}
              sx={{
                mb: 2.2,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  fontSize: "0.9rem",
                  bgcolor: mode === "light" ? "#f9fafb" : "rgba(255,255,255,0.03)",
                  transition: "box-shadow 0.2s",
                  "& fieldset": { borderColor: mode === "light" ? "#e5e7eb" : "rgba(255,255,255,0.08)" },
                  "&:hover fieldset": { borderColor: "#F26522" },
                  "&.Mui-focused fieldset": { borderColor: "#F26522", borderWidth: "1.5px" },
                  "&.Mui-focused": { boxShadow: "0 0 0 3px rgba(242,101,34,0.10)" },
                },
              }}
            />

            {/* Password */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.7 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.82rem", color: mode === "light" ? "#374151" : "#94a3b8" }}>
                Password
              </Typography>
            </Box>
            <TextField
              required fullWidth name="password" placeholder="••••••••"
              type={showPassword ? "text" : "password"} id="password"
              autoComplete="current-password" variant="outlined"
              value={password} onChange={(e) => setPassword(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlinedIcon sx={{ color: "text.secondary", fontSize: 18 }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword((v) => !v)} edge="end" size="small" sx={{ color: "text.secondary" }}>
                        {showPassword ? <VisibilityOffOutlinedIcon fontSize="small" /> : <VisibilityOutlinedIcon fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
              sx={{
                mb: 0.5,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  fontSize: "0.9rem",
                  bgcolor: mode === "light" ? "#f9fafb" : "rgba(255,255,255,0.03)",
                  transition: "box-shadow 0.2s",
                  "& fieldset": { borderColor: mode === "light" ? "#e5e7eb" : "rgba(255,255,255,0.08)" },
                  "&:hover fieldset": { borderColor: "#F26522" },
                  "&.Mui-focused fieldset": { borderColor: "#F26522", borderWidth: "1.5px" },
                  "&.Mui-focused": { boxShadow: "0 0 0 3px rgba(242,101,34,0.10)" },
                },
              }}
            />

            {/* Sign In button */}
            <Button
              type="submit" fullWidth variant="contained" disabled={loading}
              sx={{
                mt: 3, mb: 2, py: 1.55, fontSize: "0.95rem", fontWeight: 700,
                borderRadius: "10px", textTransform: "none",
                background: "linear-gradient(90deg, #e8581a 0%, #F26522 50%, #ff8c42 100%)",
                backgroundSize: "200% auto",
                boxShadow: "0 4px 20px rgba(242,101,34,0.35)",
                transition: "all 0.3s ease",
                "&:hover": {
                  backgroundPosition: "right center",
                  boxShadow: "0 6px 28px rgba(242,101,34,0.50)",
                  transform: "translateY(-1px)",
                },
                "&:active": { transform: "translateY(0)", boxShadow: "0 2px 12px rgba(242,101,34,0.3)" },
              }}
            >
              {loading ? <CircularProgress size={22} color="inherit" /> : "Sign In"}
            </Button>

            <Divider sx={{ my: 0.5, borderColor: mode === "light" ? "rgba(0,0,0,0.06)" : "rgba(255,255,255,0.06)" }} />

            <Box textAlign="center" mt={2}>
              <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.85rem" }}>
                Don't have an account?{" "}
                <Link component={RouterLink} to="/signup" underline="none"
                  sx={{ color: "#F26522", fontWeight: 700, "&:hover": { color: "#d95a18" } }}
                >
                  Sign up free
                </Link>
              </Typography>
            </Box>
          </Box>
        </Box>
        </LiquidGlassCard>
      </Box>
    </Box>
  );
}
